<template>
    <div class="min-h-40 max-w-full mx-auto relative">
        <header
            class="flex items-center justify-center relative w-full mx-auto"
            :class="{ 'xl:w-full': video.status !== 'processed' }"
        >
            <button
                data-testid="delete-video-button"
                class="flex items-center absolute right-0 text-red-500 mb-4 pr-3 | hover:text-red-700 | transition duration-150"
                @click="deleteVideo"
                v-if="video.status === 'processed'"
            >
                <icon class="mr-2" name="regular/trash-1" />
                <span v-text="$t('communications.video.deleteVideo')" />
            </button>
            <activix-spinner
                :size="45"
                class="absolute my-96 -mb-96 z-50"
                v-if="clickStopRecording && video.status === 'readyToRecord'"
            />
            <div
                class="absolute text-xl px-2 my-22 py-3 -mb-36 z-50 bg-white w-full text-center rounded-lg | lg:w-1/2"
                v-text="$t('communications.video.statusMessage.chooseThumbnail')"
                v-if="video.status == 'recordingStopped'"
            />
        </header>

        <div
            class="flex items-center justify-center mb-2 | lg:mb-0"
            :class="{ 'min-h-48': video.status !== 'processed' }"
            v-show="video.status !== 'processed'"
        >
            <section>
                <!-- home -->
                <div
                    class="flex flex-col relative"
                    :class="[
                        clickRecord || ['readyToRecord', 'recordingStopped'].includes(video.status)
                            ? 'w-full'
                            : '',
                        {
                            'h-0 w-0 opacity-0': ['uploading', 'uploaded', 'processed'].includes(
                                video.status
                            ),
                        },
                    ]"
                >
                    <div
                        class="overflow-hidden"
                        :class="[recorderClass, 'ziggeoRecorder']"
                        ref="ziggeoRecorder"
                    />
                    <activix-spinner
                        :size="45"
                        class="w-full absolute mx-auto text-center mt-16"
                        v-if="clickRecord"
                    />
                    <button
                        class="flex items-center absolute bg-white left-0 top-0 ml-0 mb-4 -mt-10 | lg:-mt-11 | hover:text-gray-700 | transition duration-150"
                        @click="resetVideo"
                        v-if="['readyToRecord', 'recordingStopped'].includes(video.status)"
                    >
                        <icon class="mr-2 flip-x" name="regular/arrow-right-1" />
                        <span v-text="$t('communications.video.resetVideo')" />
                    </button>
                </div>

                <!-- uploading -->
                <div
                    class="flex flex-col w-96 mb-3"
                    v-if="['uploading', 'uploaded'].includes(video.status)"
                >
                    <div class="flex items-center justify-between">
                        <template v-if="video.status === 'uploading'">
                            <div
                                class="mb-1 text-gray-600 uppercase tracking-wider"
                                v-text="$t('communications.video.statusMessage.uploading')"
                            />
                            <div class="text-gray-600" v-text="progress" />
                        </template>
                        <template v-if="video.status === 'uploaded'">
                            <div
                                data-testid="processing-status"
                                class="mb-1 text-gray-600 uppercase tracking-wider"
                                v-text="$t('communications.video.statusMessage.uploaded')"
                            />
                            <activix-spinner
                                data-testid="processing-spinner"
                                class="mb-1"
                                :size="15"
                                :line-size="2"
                            />
                        </template>
                    </div>
                    <div class="flex items-center flex-col justify-center">
                        <div class="rounded-md bg-gray-300 h-2 w-full | lg:10/12">
                            <div class="rounded-md h-2 bg-blue-500" :style="{ width: progress }" />
                        </div>
                        <div
                            data-testid="delayed-processing-message"
                            class="flex flex-col items-center gap-3 mt-5 text-center text-gray-600"
                            v-if="video.status === 'uploaded' && video.processingDelayed"
                        >
                            <p
                                v-text="$t('communications.video.statusMessage.processingDelayed')"
                            />
                            <button
                                data-testid="cancel-processing-button"
                                class="text-red-500 hover:text-red-700 transition duration-150"
                                type="button"
                                :disabled="cancellingProcessing"
                                @click="cancelDelayedProcessing"
                                v-text="
                                    $t(
                                        cancellingProcessing
                                            ? 'communications.video.statusMessage.cancellingProcessing'
                                            : 'communications.video.statusMessage.cancelAndRetry'
                                    )
                                "
                            />
                        </div>
                    </div>
                </div>
            </section>

            <div v-if="showRecordingButtons">
                <div class="flex items-center space-x-6">
                    <button
                        style="height: 19rem; width: 22rem"
                        class="bg-white shadow-md flex flex-col items-center justify-center text-xl text-gray-600 font-sans border border-gray-100 rounded hover:text-blue-600 hover:border-blue-500 hover:shadow-lg | transition duration-150"
                        @click="onClickRecord"
                    >
                        <div
                            class="text-2xl mb-4 mt-3"
                            v-text="$t('communications.video.record')"
                        />
                        <webcam-streaming1 />
                    </button>
                    <button
                        style="height: 19rem; width: 22rem"
                        class="bg-white shadow-md flex flex-col items-center justify-center text-xl text-gray-600 font-sans border border-gray-100 rounded hover:text-blue-600 hover:border-blue-500 hover:shadow-lg | transition duration-150"
                        @click="onClickUpload"
                    >
                        <div
                            class="text-2xl mb-4 mt-3"
                            v-text="$t('communications.video.upload')"
                        />
                        <upload2 />
                    </button>
                </div>
                <div
                    class="text-center text-sm mt-10 text-gray-500"
                    v-text="$t('communications.video.description')"
                />
            </div>
        </div>
        <div
            class="relative h-0 w-full | lg:10/12 bg-gray-800 rounded-md"
            style="padding-bottom: 56.25%"
            v-if="video.status === 'processed'"
        >
            <!-- 16:9 -->
            <video controls class="absolute top-0 left-0 w-full h-full rounded-lg" ref="video">
                <source :src="videoFile" type="video/mp4" />
            </video>
        </div>
    </div>
</template>

<script>
    // Illustrations
    import WebcamStreaming1 from '@/assets/svg/illustrations/webcam-streaming-1.svg';
    import Upload2 from '@/assets/svg/illustrations/upload-2.svg';

    export default {
        emits: ['videoCancelled'],

        components: {
            WebcamStreaming1,
            Upload2,
        },

        props: {
            ziggeoId: {
                type: String,
            },
            processed: {
                type: Boolean,
                default: false,
            },
            user: {
                type: Object,
            },
            leadId: {
                type: Number,
            },
        },

        data() {
            return {
                clickRecord: false,
                clickStopRecording: false,
                cancellingProcessing: false,
                componentDisposed: false,
            };
        },

        computed: {
            recorderClass() {
                if (this.clickRecord) {
                    return 'invisible h-40';
                }

                if (['readyToRecord', 'recordingStopped'].includes(this.video.status)) {
                    return 'video-recorder-visible';
                }

                return '';
            },

            videoId() {
                return this.leadId;
            },

            showRecordingButtons() {
                return this.video.status === '' && !this.clickRecord;
            },

            videoFile() {
                const ziggeoId = this.ziggeoId ? this.ziggeoId : this.video.ziggeoId;
                return this.$ziggeo.getVideoFile(ziggeoId);
            },

            video() {
                return this.$ziggeo.getVideoById(this.videoId) || { status: '' };
            },

            progress() {
                return `${this.video.progress}%`;
            },
        },

        watch: {
            'video.status': {
                handler(newValue, oldValue) {
                    if (newValue === oldValue) {
                        return;
                    }

                    switch (newValue) {
                        case 'readyToRecord':
                            this.clickRecord = false;
                            this.mountRecorderStopClick();
                            this.setRecorderStyle();
                            break;
                        case '':
                            this.clickRecord = false;
                            this.clickStopRecording = false;
                            this.$ziggeo.mountRecorder(this.videoId, this.$refs.ziggeoRecorder);
                            this.mountRecorderClick();
                            break;
                    }
                },
            },

            ziggeoId(newValue, oldValue) {
                if (
                    !oldValue &&
                    newValue &&
                    !['uploading', 'readyToRecord'].includes(this.video.status)
                ) {
                    this.clickRecord = false;

                    if (this.$refs.ziggeoRecorder.querySelector('.ba-videorecorder-container')) {
                        this.updateVideo({
                            status: this.processed ? 'processed' : '',
                            ziggeoId: this.ziggeoId,
                        });
                    } else {
                        this.createRecorder();
                    }
                }
            },
        },

        methods: {
            onClickRecord() {
                document.querySelector('.ba-videorecorder-chooser-button-0').click();
            },

            onClickUpload() {
                document
                    .querySelector(
                        '.ba-videorecorder-chooser-button-1 .ba-videorecorder-chooser-file:first-child'
                    )
                    .click();
            },

            setRecorderStyle() {
                const el = document.querySelector('div[data-selector=video-recorder-container]');
                if (!el) {
                    return;
                }

                el.style.width = '100%';
                el.style.height = '100%';
            },

            onClickRecorder() {
                this.$behavior.track('VideoPresenting', { action: 'record' });
                this.clickRecord = true;
            },

            onClickStopRecorder() {
                if (!this.$refs.ziggeoRecorder) {
                    return;
                }

                if (this.$refs.ziggeoRecorder.querySelector('.ba-videorecorder-disabled')) {
                    return;
                }

                this.clickStopRecording = true;
            },

            updateVideo(payload) {
                this.$ziggeo.updateVideo(this.videoId, payload);
            },

            async createRecorder(initialZiggeoId = this.ziggeoId) {
                const recorderElement = this.$refs.ziggeoRecorder;

                if (this.componentDisposed || !recorderElement) {
                    return;
                }

                await this.$ziggeo.createRecorder(
                    this.videoId,
                    recorderElement,
                    initialZiggeoId,
                    this.user
                );

                if (this.componentDisposed) {
                    return;
                }

                this.mountRecorderClick();
            },

            async cancelDelayedProcessing() {
                this.cancellingProcessing = true;

                try {
                    const processingToken = this.video.ziggeoId || this.ziggeoId;
                    const deleted = await this.$ziggeo.deleteVideo(this.videoId, processingToken);

                    if (!deleted) {
                        this.$notify.error(
                            this.$t('communications.video.statusMessage.cancellationFailed')
                        );
                        return;
                    }

                    this.$emit('videoCancelled', processingToken);

                    const recorderElement = this.$refs.ziggeoRecorder;

                    if (this.componentDisposed || !recorderElement) {
                        return;
                    }

                    this.clickRecord = false;
                    this.clickStopRecording = false;
                    recorderElement.replaceChildren();
                    await this.createRecorder('');
                } catch {
                    this.$notify.error(
                        this.$t('communications.video.statusMessage.cancellationFailed')
                    );
                } finally {
                    this.cancellingProcessing = false;
                }
            },

            async mountRecorderClick() {
                await this.$nextTick();

                if (!this.$refs.ziggeoRecorder) {
                    return;
                }

                const recordButton = this.$refs.ziggeoRecorder.querySelector(
                    '.ba-videorecorder-chooser-button-0'
                );

                if (recordButton) {
                    recordButton.addEventListener('click', this.onClickRecorder);
                }
            },

            mountRecorderStopClick() {
                const recordButton = this.$refs.ziggeoRecorder.querySelector(
                    '[data-selector="stop-primary-button"]'
                );
                if (recordButton) {
                    recordButton.addEventListener('click', this.onClickStopRecorder);
                }
            },

            resetVideo() {
                this.$ziggeo.resetVideo(this.videoId);
                this.mountRecorderClick();
            },

            async deleteVideo() {
                this.clickRecord = false;
                this.clickStopRecording = false;
                const deletionToken = this.video.ziggeoId || this.ziggeoId;
                const deleted = await this.$ziggeo.deleteVideo(this.videoId, deletionToken);

                if (deleted && deletionToken) {
                    this.$emit('videoCancelled', deletionToken);
                }

                this.mountRecorderClick();
            },

            onClosed() {
                if (['uploaded', 'uploading'].includes(this.video.status)) {
                    this.$behavior.track('VideoPresenting', {
                        action: 'exit-before',
                    });
                    this.$notify.info(this.$t('communications.video.warning'));
                }

                if (this.$refs.video) {
                    this.$refs.video.pause();
                }

                const recordStartButton = this.$refs.ziggeoRecorder?.querySelector(
                    '.ba-videorecorder-chooser-button-0'
                );
                const recordStopButton = this.$refs.ziggeoRecorder?.querySelector(
                    '[data-selector="stop-primary-button"]'
                );

                if (recordStartButton) {
                    recordStartButton.removeEventListener('click', this.onClickRecorder);
                }
                if (recordStopButton) {
                    recordStopButton.removeEventListener('click', this.onClickStopRecorder);
                }

                if (['', 'readyToRecord', 'recordingStopped'].includes(this.video.status)) {
                    this.deleteVideo();
                }
            },
        },

        async mounted() {
            this.$nextTick(() => {
                this.createRecorder();
            });
        },

        beforeUnmount() {
            this.componentDisposed = true;
            this.$ziggeo.clearVideoLifecycle(this.videoId);
            this.onClosed();
        },
    };
</script>
